import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Shield, Clock, FileText, GraduationCap, ChevronRight, Star, ChevronDown, Mail, Lock, Sparkles, Globe, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Five Elements data
const elements = [
  { name: "Wood", emoji: "🌿", className: "wood", color: "rgb(34, 197, 94)" },
  { name: "Fire", emoji: "🔥", className: "fire", color: "rgb(239, 68, 68)" },
  { name: "Earth", emoji: "🌍", className: "earth", color: "rgb(234, 179, 8)" },
  { name: "Metal", emoji: "⚔️", className: "metal", color: "rgb(148, 163, 184)" },
  { name: "Water", emoji: "💧", className: "water", color: "rgb(59, 130, 246)" }
];

// Features data
const features = [
  {
    icon: "🌟",
    title: "Astrological Elemental Foundation",
    description: "Your sun sign, life path number, Chinese zodiac and vital planet — integrated into a unique neuroenergetic profile."
  },
  {
    icon: "☯️",
    title: "Five Elements Diagnosis (Wu Xing)",
    description: "Complete constitutional analysis: primary, secondary, tertiary and supporting elements with organ systems and emotional roots."
  },
  {
    icon: "🧬",
    title: "Ancestral Epigenetic Resonance",
    description: "Reading of your 4 grandparents' lineage: emotional inheritance, constitutional patterns and ancestral integration rituals."
  },
  {
    icon: "🥗",
    title: "Elemental Nutrition Protocol",
    description: "Personalized nutrition plan based on your constitution and blood type with element-specific food mapping."
  },
  {
    icon: "🏋️",
    title: "Weekly Movement Architecture",
    description: "Personalized exercise routine aligned with your elements: strength, yoga, walking, breathwork and recovery."
  },
  {
    icon: "🧠",
    title: "Life Purpose & Affirmations",
    description: "Psychological integration, stress management, daily rituals and affirmations for elemental balance."
  }
];

// Steps data
const steps = [
  {
    num: 1,
    title: "Enter Your Data",
    description: "Name, your 4 grandparents' surnames, birth date and time, blood type."
  },
  {
    num: 2,
    title: "Secure Payment",
    description: "Process your payment securely with Stripe. We accept all major cards."
  },
  {
    num: 3,
    title: "AI Generation",
    description: "Our medical AI agent trained by a doctor generates your personalized reading."
  },
  {
    num: 4,
    title: "Receive Your Blueprint",
    description: "In less than 24 hours you'll receive your 15+ page PDF report via email."
  }
];

// Testimonials data
const testimonials = [
  {
    text: "My Five Elements Reading was revelatory. I understood why my body reacted the way it did and finally found a nutrition protocol that actually works for me.",
    author: "Edward Newland",
    meta: "Life Path 11 · Metal-Water-Fire"
  },
  {
    text: "Incredible how they integrated my ancestral lineage with my elemental constitution. I reconnected with my roots in a way I never imagined possible.",
    author: "Claudio Vidal",
    meta: "Life Path 3 · Earth Tiger · Fire-Earth"
  },
  {
    text: "The level of detail is impressive. 15+ personalized pages with daily rituals, nutrition, movement and affirmations that truly resonate with me.",
    author: "Diogo",
    meta: "Life Path 11 · Aquarius · Fire Rooster"
  }
];

// FAQ data
const faqs = [
  {
    question: "Is this a medical diagnosis?",
    answer: "Yes. The NeuroVitale™ Five Elements Reading is an initial elemental medical assessment created by Dr. Camila Trabucco, MD. It maps your constitutional pattern, stress axes and lifestyle priorities as a structured starting point of Elemental Medicine. In private coaching, this blueprint is later combined with laboratory work, anamnesis and in-person assessment. It does not replace emergency care or your local physician."
  },
  {
    question: "How long does it take to receive my reading?",
    answer: "Your personalized 15+ page reading is delivered via email in PDF format in less than 24 hours after completing your payment and submitting your data. The process is fully automated."
  },
  {
    question: "Why do you need my grandparents' surnames?",
    answer: "Each ancestral lineage carries a specific elemental frequency based on its geographical, cultural and linguistic origin. Your 4 grandparents' surnames allow us to trace your epigenetic inheritance and constitutional tendencies inherited from each family branch."
  },
  {
    question: "What if I don't know my exact birth time?",
    answer: "Birth time is important to determine the active meridian at birth. If you don't know it exactly, you can check your birth certificate or ask your family. If you only have an approximation, enter it anyway."
  },
  {
    question: "Do you accept international payments?",
    answer: "Yes. We accept all international credit and debit cards (Visa, Mastercard, American Express) through Stripe, the world's most secure payment platform."
  }
];

// Language options for PDF
const languageOptions = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "pt", name: "Português", flag: "🇵🇹" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "hi", name: "हिन्दी (Hindi)", flag: "🇮🇳" },
  { code: "zh", name: "中文 (Chinese)", flag: "🇨🇳" }
];

export default function LandingPage() {
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [intakeLoading, setIntakeLoading] = useState(false);
  const intakeFormRef = useRef(null);
  
  // Feedback modal state
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSessionId, setFeedbackSessionId] = useState("");
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackData, setFeedbackData] = useState({
    process_rating: 5,
    blueprint_helpful: true,
    most_impactful_part: "",
    additional_comments: ""
  });
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    birthDate: "",
    birthTime: "",
    birthPlace: "",
    bloodType: "",
    pdfLanguage: "en",
    pgf1: "",
    pgf2: "",
    pgm1: "",
    pgm2: "",
    mgf1: "",
    mgf2: "",
    mmf1: "",
    mmf2: "",
    familyStory: ""
  });

  // GDPR consent state
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState(false);

  // Check for feedback URL param on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const feedbackId = urlParams.get('feedback');
    if (feedbackId) {
      setFeedbackSessionId(feedbackId);
      setShowFeedback(true);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, bloodType: value }));
  };

  const handleLanguageChange = (value) => {
    setFormData(prev => ({ ...prev, pdfLanguage: value }));
  };

  // GDPR consent labels by language
  const getConsentLabel = (lang) => {
    const labels = {
      es: 'He leido y acepto la <a href="https://www.visitamedicaonline.com/es/politicas-de-privacidad/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Politica de Privacidad</a>, el <a href="https://www.visitamedicaonline.com/es/aviso-legal/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Aviso Legal</a> y las <a href="https://www.visitamedicaonline.com/es/condiciones-de-contratacion/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Condiciones de Contratacion</a> de Visita Medica Online. Consiento el tratamiento de mis datos personales para la generacion de mi diagnostico NeuroVitale conforme al RGPD.',
      en: 'I have read and accept the <a href="https://www.visitamedicaonline.com/es/politicas-de-privacidad/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Privacy Policy</a>, <a href="https://www.visitamedicaonline.com/es/aviso-legal/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Legal Notice</a> and <a href="https://www.visitamedicaonline.com/es/condiciones-de-contratacion/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Terms of Service</a> of Visita Medica Online. I consent to the processing of my personal data for the generation of my NeuroVitale blueprint in accordance with GDPR.',
      pt: 'Li e aceito a <a href="https://www.visitamedicaonline.com/es/politicas-de-privacidade/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Politica de Privacidade</a>, o <a href="https://www.visitamedicaonline.com/es/aviso-legal/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Aviso Legal</a> e as <a href="https://www.visitamedicaonline.com/es/condicoes-de-contratacao/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Condicoes de Contratacao</a> da Visita Medica Online. Consinto no tratamento dos meus dados pessoais para a geracao do meu diagnostico NeuroVitale em conformidade com o RGPD.',
      fr: 'J\'ai lu et j\'accepte la <a href="https://www.visitamedicaonline.com/es/politique-de-confidentialite/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Politique de Confidentialite</a>, les <a href="https://www.visitamedicaonline.com/es/mentions-legales/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Mentions Legales</a> et les <a href="https://www.visitamedicaonline.com/es/conditions-generales/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Conditions Generales</a> de Visita Medica Online. Je consens au traitement de mes donnees personnelles pour la generation de mon bilan NeuroVitale conformement au RGPD.',
      de: 'Ich habe die <a href="https://www.visitamedicaonline.com/es/datenschutzrichtlinie/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Datenschutzrichtlinie</a>, den <a href="https://www.visitamedicaonline.com/es/rechtlicher-hinweis/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">rechtlichen Hinweis</a> und die <a href="https://www.visitamedicaonline.com/es/agb/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">Allgemeinen Geschaeftsbedingungen</a> von Visita Medica Online gelesen und akzeptiert. Ich stimme der Verarbeitung meiner personenbezogenen Daten zur Erstellung meines NeuroVitale-Blueprints gemass DSGVO zu.',
      hi: 'मैंने Visita Medica Online की <a href="https://www.visitamedicaonline.com/es/politicas-de-privacidad/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">गोपनीयता नीति</a>, <a href="https://www.visitamedicaonline.com/es/aviso-legal/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">कानूनी सूचना</a> और <a href="https://www.visitamedicaonline.com/es/condiciones-de-contratacion/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">सेवा की शर्तें</a> पढ़ ली हैं और स्वीकार करता/करती हूं। मैं GDPR के अनुसार अपने NeuroVitale ब्लूप्रिंट के निर्माण के लिए अपने व्यक्तिगत डेटा के प्रसंस्करण की सहमति देता/देती हूं।',
      zh: '我已阅读并接受 Visita Medica Online 的<a href="https://www.visitamedicaonline.com/es/politicas-de-privacidad/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">隐私政策</a>、<a href="https://www.visitamedicaonline.com/es/aviso-legal/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">法律声明</a>和<a href="https://www.visitamedicaonline.com/es/condiciones-de-contratacion/" target="_blank" class="text-[#C9A84C] underline hover:text-[#FCD34D]">服务条款</a>。我同意根据 GDPR 处理我的个人数据以生成我的 NeuroVitale 蓝图。'
    };
    return labels[lang] || labels.en;
  };

  const scrollToIntake = () => {
    intakeFormRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Feedback form handlers
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackSessionId) return;
    
    setFeedbackLoading(true);
    try {
      const response = await fetch(`${API}/neurovitale/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: feedbackSessionId,
          ...feedbackData
        })
      });
      
      if (response.ok) {
        toast.success("Thank you for your feedback! It helps us improve.");
        setShowFeedback(false);
        // Remove feedback param from URL
        window.history.replaceState({}, '', window.location.pathname);
      } else {
        toast.error("Failed to submit feedback. Please try again.");
      }
    } catch (error) {
      toast.error("Connection error. Please try again.");
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    
    setNewsletterLoading(true);
    try {
      const response = await fetch(`${API}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newsletterEmail })
      });
      
      if (response.ok) {
        toast.success("Welcome to NeuroVitale! Check your inbox for updates.");
        setNewsletterEmail("");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error("Connection error. Please try again.");
    } finally {
      setNewsletterLoading(false);
    }
  };

  const handleIntakeSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const required = ["fullName", "email", "birthDate", "birthTime", "birthPlace", "pgf1", "pgf2", "pgm1", "pgm2", "mgf1", "mgf2", "mmf1", "mmf2"];
    const missing = required.filter(field => !formData[field]);
    
    if (missing.length > 0) {
      toast.error("Please fill in all required fields.");
      return;
    }

    // Validate GDPR consent
    if (!consentChecked) {
      setConsentError(true);
      const lang = formData.pdfLanguage || 'en';
      const errorMessages = {
        es: "Debes aceptar los términos para continuar",
        pt: "Você deve aceitar os termos para continuar",
        fr: "Vous devez accepter les conditions pour continuer",
        de: "Sie müssen die Bedingungen akzeptieren, um fortzufahren",
        en: "You must accept the terms to continue",
        hi: "जारी रखने के लिए आपको शर्तें स्वीकार करनी होंगी",
        zh: "您必须接受条款才能继续"
      };
      toast.error(errorMessages[lang] || errorMessages.en);
      return;
    }
    setConsentError(false);
    
    setIntakeLoading(true);
    try {
      const submissionData = {
        ...formData,
        consent_accepted: true,
        consent_timestamp: new Date().toISOString(),
        consent_language: formData.pdfLanguage || 'en'
      };

      const response = await fetch(`${API}/neurovitale/intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(submissionData)
      });
      
      const data = await response.json();
      
      if (data.ok && data.paymentLink) {
        toast.success("Data saved! Redirecting to secure payment...");
        // Redirect to Stripe Payment Link
        setTimeout(() => {
          window.location.href = data.paymentLink;
        }, 1500);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error("Connection error. Please try again.");
    } finally {
      setIntakeLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      {/* Background Orbs */}
      <div className="bg-orbs">
        <div className="orb" />
        <div className="orb" />
        <div className="orb" />
      </div>

      {/* Sticky Nav */}
      <nav className="sticky top-0 z-50 glass" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="https://www.visitamedicaonline.com" target="_blank" rel="noopener noreferrer" data-testid="logo-link">
            <div className="flex flex-col">
              <span className="font-heading text-2xl font-bold text-gold-gradient bg-gradient-to-r from-[#C9A84C] via-[#FCD34D] to-[#C9A84C] bg-clip-text text-transparent">
                NEUROVITALE™
              </span>
              <span className="text-xs text-[#64748B] tracking-wider">by Visita Médica Online</span>
            </div>
          </a>
          <Button 
            onClick={scrollToIntake}
            className="btn-primary hidden md:flex items-center gap-2"
            data-testid="nav-cta-btn"
          >
            Get My Blueprint <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-16 pb-24 px-6" data-testid="hero-section">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-gold mb-8">
              <span className="w-2 h-2 rounded-full bg-[#C9A84C] animate-pulse" />
              <span className="text-sm text-[#C9A84C]">Ancestral Holistic Medicine — Created by a Medical Doctor</span>
            </div>

            {/* Headline */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-6 text-[#E2E8F0]">
              Discover Your <em className="text-gold-gradient bg-gradient-to-r from-[#C9A84C] via-[#FCD34D] to-[#C9A84C] bg-clip-text text-transparent not-italic">NeuroEnergetic</em>
              <br />Five-Elements Blueprint
            </h1>

            {/* Subheadline */}
            <p className="text-base md:text-lg text-[#94A3B8] max-w-3xl mx-auto mb-12 leading-relaxed">
              Receive a personalized 15+ page report that integrates astrology, numerology, 
              the 5 Elements of Chinese Medicine, your ancestral lineage and protocols for 
              nutrition, movement and life purpose.
            </p>
          </motion.div>

          {/* Five Elements Ring */}
          <motion.div 
            className="flex justify-center gap-4 md:gap-8 mb-12 flex-wrap"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {elements.map((el, i) => (
              <motion.div
                key={el.name}
                variants={fadeUp}
                className={`element-orb ${el.className}`}
                data-testid={`element-${el.className}`}
              >
                <span className="text-2xl md:text-3xl">{el.emoji}</span>
                <span className="text-[10px] text-[#94A3B8] mt-1">{el.name}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* Price Badge */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mb-8"
          >
            <div className="price-badge inline-flex" data-testid="price-badge">
              <span className="price-old">€39.00</span>
              <span className="price-new">€29.00</span>
              <span className="text-xs text-[#94A3B8] mt-1">EUR — Launch Price</span>
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <Button 
              onClick={scrollToIntake}
              className="btn-primary text-lg px-10 py-4 inline-flex items-center gap-2"
              data-testid="hero-cta-btn"
            >
              Get My Blueprint
              <ChevronRight className="w-5 h-5" />
            </Button>
          </motion.div>

          {/* Trust Row */}
          <motion.div 
            className="flex flex-wrap justify-center gap-6 md:gap-10 mt-12 text-sm text-[#94A3B8]"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {[
              { icon: Shield, text: "Secure payment with Stripe" },
              { icon: Clock, text: "Delivered in less than 24h" },
              { icon: FileText, text: "15+ personalized pages" },
              { icon: GraduationCap, text: "Doctor-created method" }
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp} className="flex items-center gap-2">
                <item.icon className="w-4 h-4 text-[#C9A84C]" />
                <span>{item.text}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-24 px-6" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="inline-flex items-center gap-2 text-sm text-[#C9A84C] tracking-wider uppercase mb-4">
              <span className="w-8 h-px bg-[#C9A84C]" />
              Your Blueprint Includes
              <span className="w-8 h-px bg-[#C9A84C]" />
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#E2E8F0]">
              A Complete Map of Your Ancestral Design
            </h2>
            <p className="text-[#94A3B8] mt-4 max-w-2xl mx-auto">
              Each reading is unique, generated from your birth data, ancestral lineage and blood type.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {features.map((feature, i) => (
              <motion.div key={i} variants={fadeUp} className="feature-card" data-testid={`feature-card-${i}`}>
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="font-heading text-xl font-semibold text-[#E2E8F0] mb-3">{feature.title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-6 relative" style={{ background: "rgba(201,168,76,0.02)" }} data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="inline-flex items-center gap-2 text-sm text-[#C9A84C] tracking-wider uppercase mb-4">
              <span className="w-8 h-px bg-[#C9A84C]" />
              Process
              <span className="w-8 h-px bg-[#C9A84C]" />
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#E2E8F0]">
              How It Works
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {steps.map((step, i) => (
              <motion.div key={i} variants={fadeUp} className="text-center" data-testid={`step-${i}`}>
                <div className="step-number mx-auto mb-4">{step.num}</div>
                <h4 className="font-heading text-lg font-semibold text-[#E2E8F0] mb-2">{step.title}</h4>
                <p className="text-sm text-[#94A3B8]">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Creator Section */}
      <section className="py-24 px-6" data-testid="creator-section">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="inline-flex items-center gap-2 text-sm text-[#C9A84C] tracking-wider uppercase mb-4">
              <span className="w-8 h-px bg-[#C9A84C]" />
              Creator
              <span className="w-8 h-px bg-[#C9A84C]" />
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#E2E8F0]">
              Medicine with Consciousness
            </h2>
          </motion.div>

          <motion.div 
            className="glass rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-start gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#C9A84C]/20 to-[#856E2D]/20 flex items-center justify-center text-6xl border border-[#C9A84C]/30 flex-shrink-0">
              👩‍⚕️
            </div>
            <div>
              <h3 className="font-heading text-2xl font-semibold text-[#E2E8F0] mb-1">Dr. Camila Trabucco</h3>
              <p className="text-[#C9A84C] text-sm mb-4">MD — Ancestral Holistic Medicine · NeuroVitale™</p>
              <p className="text-[#94A3B8] leading-relaxed mb-4">
                The NeuroVitale™ method integrates neuroscience, traditional Chinese medicine, medical astrology, 
                numerology and positive epigenetics into a unique system designed to decode your individual frequency.
              </p>
              <p className="text-[#94A3B8] leading-relaxed mb-4">
                This reading is an initial elemental medical assessment: it helps map your constitutional pattern, 
                stress axes and lifestyle priorities before ordering labs or doing a full physical examination. 
                In private coaching, this blueprint is later combined with laboratory work, full anamnesis and 
                in-person physical assessment, which can extend into high-end 1:1 programs.
              </p>
              <p className="text-[#94A3B8] leading-relaxed mb-4">
                It does not replace emergency care or your local physician; it is the structured starting point of 
                Elemental Medicine, the framework I created to understand how intrinsic energy drives biology, 
                longevity and performance.
              </p>
              <p className="text-[#94A3B8] leading-relaxed italic border-l-2 border-[#C9A84C]/30 pl-4">
                Before medical school I spent years as a competitive swimmer and triathlon trainee, and my family 
                has run gyms and pools for decades. I've been teaching children how to breathe, float and move in 
                water since I was 11. This is why NeuroVitale™ is not only theoretical medicine: it's built from 
                lived experience in training, food, movement and nervous system mastery.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-6" style={{ background: "rgba(201,168,76,0.02)" }} data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="inline-flex items-center gap-2 text-sm text-[#C9A84C] tracking-wider uppercase mb-4">
              <span className="w-8 h-px bg-[#C9A84C]" />
              Experiences
              <span className="w-8 h-px bg-[#C9A84C]" />
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#E2E8F0]">
              Real Transformations
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {testimonials.map((testimonial, i) => (
              <motion.div key={i} variants={fadeUp} className="testimonial-card" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 text-[#C9A84C] mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-[#E2E8F0] mb-6 relative z-10">"{testimonial.text}"</p>
                <div className="text-[#E2E8F0] font-semibold">{testimonial.author}</div>
                <div className="text-xs text-[#C9A84C]">{testimonial.meta}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 px-6" data-testid="newsletter-section">
        <div className="max-w-xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <Mail className="w-12 h-12 text-[#C9A84C] mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-semibold text-[#E2E8F0] mb-3">
              Join the NeuroVitale Newsletter
            </h3>
            <p className="text-[#94A3B8] text-sm mb-6">
              Receive ancestral wisdom, elemental insights and exclusive content directly to your inbox.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="your@email.com"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="form-input flex-1"
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit" 
                disabled={newsletterLoading}
                className="btn-primary whitespace-nowrap"
                data-testid="newsletter-submit-btn"
              >
                {newsletterLoading ? "..." : "Subscribe"}
              </Button>
            </form>
            <p className="text-xs text-[#64748B] mt-3">
              MailPoet-compatible list. Unsubscribe anytime.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intake Form Section */}
      <section ref={intakeFormRef} className="py-24 px-6 relative" id="order" data-testid="intake-section">
        <div className="max-w-3xl mx-auto">
          <motion.div 
            className="glass rounded-3xl overflow-hidden"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            {/* Form Header */}
            <div className="text-center py-10 px-6 border-b border-white/10">
              <h2 className="font-heading text-3xl font-semibold text-[#E2E8F0] mb-2">
                Your Five Elements Reading
              </h2>
              <p className="text-[#94A3B8] mb-4">
                Complete your data to generate your personalized NeuroEnergetic Blueprint
              </p>
              <div className="price-badge inline-flex">
                <span className="price-new">€29.00</span>
                <span className="text-xs text-[#94A3B8]">EUR</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleIntakeSubmit} className="p-6 md:p-10 space-y-8">
              {/* Personal Data */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-8 h-px bg-[#C9A84C]" />
                  <span className="text-sm text-[#C9A84C] tracking-wider uppercase">Personal Data</span>
                  <span className="flex-1 h-px bg-white/10" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Full Name <span className="text-red-400">*</span></Label>
                    <Input
                      name="fullName"
                      placeholder="e.g. John Smith"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      data-testid="input-fullName"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Email <span className="text-red-400">*</span></Label>
                    <Input
                      type="email"
                      name="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Date of Birth <span className="text-red-400">*</span></Label>
                    <Input
                      type="date"
                      name="birthDate"
                      value={formData.birthDate}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      data-testid="input-birthDate"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Exact Time of Birth <span className="text-red-400">*</span></Label>
                    <Input
                      type="time"
                      name="birthTime"
                      value={formData.birthTime}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      data-testid="input-birthTime"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Place of Birth <span className="text-red-400">*</span></Label>
                    <Input
                      name="birthPlace"
                      placeholder="City, Country"
                      value={formData.birthPlace}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      data-testid="input-birthPlace"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Blood Type</Label>
                    <Select value={formData.bloodType} onValueChange={handleSelectChange}>
                      <SelectTrigger className="form-input" data-testid="select-bloodType">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="O+">O+</SelectItem>
                        <SelectItem value="O-">O-</SelectItem>
                        <SelectItem value="A+">A+</SelectItem>
                        <SelectItem value="A-">A-</SelectItem>
                        <SelectItem value="B+">B+</SelectItem>
                        <SelectItem value="B-">B-</SelectItem>
                        <SelectItem value="AB+">AB+</SelectItem>
                        <SelectItem value="AB-">AB-</SelectItem>
                        <SelectItem value="unknown">I don't know</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Language Selector */}
                <div className="mt-4">
                  <Label className="form-label flex items-center gap-2">
                    <Globe className="w-4 h-4 text-[#C9A84C]" />
                    Blueprint Language <span className="text-red-400">*</span>
                  </Label>
                  <p className="text-xs text-[#64748B] mb-2">Choose the language for your personalized report</p>
                  <Select value={formData.pdfLanguage} onValueChange={handleLanguageChange}>
                    <SelectTrigger className="form-input" data-testid="select-pdfLanguage">
                      <SelectValue placeholder="Select language..." />
                    </SelectTrigger>
                    <SelectContent>
                      {languageOptions.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          <span className="flex items-center gap-2">
                            <span>{lang.flag}</span>
                            <span>{lang.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Ancestral Lineage */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-px bg-[#C9A84C]" />
                  <span className="text-sm text-[#C9A84C] tracking-wider uppercase">Ancestral Lineage — The 8 Surnames</span>
                  <span className="flex-1 h-px bg-white/10" />
                </div>

                <div className="glass-gold rounded-lg p-4 mb-6 text-sm text-[#94A3B8]">
                  <p>
                    🧬 Your four grandparents' surnames are essential for the ancestral epigenetic reading. 
                    Each lineage carries a specific elemental frequency. If you don't know one of them, write "Unknown".
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Paternal Grandfather */}
                  <div className="glass rounded-xl p-5">
                    <h5 className="text-sm font-semibold text-[#E2E8F0] mb-4">👴 Paternal Grandfather</h5>
                    <div className="space-y-3">
                      <div>
                        <Label className="form-label">First Surname <span className="text-red-400">*</span></Label>
                        <Input
                          name="pgf1"
                          placeholder="Father's surname"
                          value={formData.pgf1}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                          data-testid="input-pgf1"
                        />
                      </div>
                      <div>
                        <Label className="form-label">Second Surname <span className="text-red-400">*</span></Label>
                        <Input
                          name="pgf2"
                          placeholder="Mother's surname"
                          value={formData.pgf2}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                          data-testid="input-pgf2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Paternal Grandmother */}
                  <div className="glass rounded-xl p-5">
                    <h5 className="text-sm font-semibold text-[#E2E8F0] mb-4">👵 Paternal Grandmother</h5>
                    <div className="space-y-3">
                      <div>
                        <Label className="form-label">First Surname <span className="text-red-400">*</span></Label>
                        <Input
                          name="pgm1"
                          placeholder="Father's surname"
                          value={formData.pgm1}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                          data-testid="input-pgm1"
                        />
                      </div>
                      <div>
                        <Label className="form-label">Second Surname <span className="text-red-400">*</span></Label>
                        <Input
                          name="pgm2"
                          placeholder="Mother's surname"
                          value={formData.pgm2}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                          data-testid="input-pgm2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Maternal Grandfather */}
                  <div className="glass rounded-xl p-5">
                    <h5 className="text-sm font-semibold text-[#E2E8F0] mb-4">👴 Maternal Grandfather</h5>
                    <div className="space-y-3">
                      <div>
                        <Label className="form-label">First Surname <span className="text-red-400">*</span></Label>
                        <Input
                          name="mgf1"
                          placeholder="Father's surname"
                          value={formData.mgf1}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                          data-testid="input-mgf1"
                        />
                      </div>
                      <div>
                        <Label className="form-label">Second Surname <span className="text-red-400">*</span></Label>
                        <Input
                          name="mgf2"
                          placeholder="Mother's surname"
                          value={formData.mgf2}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                          data-testid="input-mgf2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Maternal Grandmother */}
                  <div className="glass rounded-xl p-5">
                    <h5 className="text-sm font-semibold text-[#E2E8F0] mb-4">👵 Maternal Grandmother</h5>
                    <div className="space-y-3">
                      <div>
                        <Label className="form-label">First Surname <span className="text-red-400">*</span></Label>
                        <Input
                          name="mmf1"
                          placeholder="Father's surname"
                          value={formData.mmf1}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                          data-testid="input-mmf1"
                        />
                      </div>
                      <div>
                        <Label className="form-label">Second Surname <span className="text-red-400">*</span></Label>
                        <Input
                          name="mmf2"
                          placeholder="Mother's surname"
                          value={formData.mmf2}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                          data-testid="input-mmf2"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Family Story */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-px bg-[#C9A84C]" />
                  <span className="text-sm text-[#C9A84C] tracking-wider uppercase">Family Narrative</span>
                  <span className="flex-1 h-px bg-white/10" />
                </div>

                <Label className="form-label">
                  Is there any important detail about your family story you want to share?
                </Label>
                <Textarea
                  name="familyStory"
                  placeholder="For example: adoptive vs biological lineages, migrations, wars, trauma, blessings, who actually raised you, who left you the house or the land, etc. This helps us understand your lived lineage, not only your biological one."
                  value={formData.familyStory}
                  onChange={handleInputChange}
                  className="form-input min-h-[120px]"
                  data-testid="input-familyStory"
                />
              </div>

              {/* GDPR Consent Checkbox */}
              <div className="pt-4" data-testid="gdpr-consent-section">
                <label className={`flex items-start gap-3 cursor-pointer p-4 rounded-lg border ${consentError ? 'border-red-500 bg-red-500/5' : 'border-[#1E293B] bg-[#0F172A]/50'}`}>
                  <input
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => { setConsentChecked(e.target.checked); setConsentError(false); }}
                    className="mt-1 w-4 h-4 rounded border-[#C9A84C] text-[#C9A84C] focus:ring-[#C9A84C] flex-shrink-0"
                    data-testid="gdpr-consent-checkbox"
                  />
                  <span className="text-xs text-[#94A3B8] leading-relaxed" dangerouslySetInnerHTML={{ __html: getConsentLabel(formData.pdfLanguage) }} />
                </label>
                {consentError && (
                  <p className="text-red-400 text-xs mt-2" data-testid="gdpr-consent-error">
                    {formData.pdfLanguage === 'es' ? 'Debes aceptar los términos para continuar' :
                     formData.pdfLanguage === 'pt' ? 'Você deve aceitar os termos para continuar' :
                     formData.pdfLanguage === 'fr' ? 'Vous devez accepter les conditions pour continuer' :
                     formData.pdfLanguage === 'de' ? 'Sie müssen die Bedingungen akzeptieren, um fortzufahren' :
                     'You must accept the terms to continue'}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={intakeLoading}
                  className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-3"
                  data-testid="intake-submit-btn"
                >
                  {intakeLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-[#0A0A0F] border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Save my data and continue to secure payment
                    </>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-2 mt-4 text-sm text-[#64748B]">
                  <Lock className="w-4 h-4" />
                  <span>Secure payment powered by Stripe. Your data is encrypted and protected.</span>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="inline-flex items-center gap-2 text-sm text-[#C9A84C] tracking-wider uppercase mb-4">
              <span className="w-8 h-px bg-[#C9A84C]" />
              FAQ
              <span className="w-8 h-px bg-[#C9A84C]" />
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#E2E8F0]">
              Clear Answers
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <Accordion type="single" collapsible className="space-y-2">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="faq-item border-b border-white/10" data-testid={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-[#E2E8F0] hover:text-[#C9A84C] py-5 font-medium">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#94A3B8] pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10" data-testid="footer">
        <div className="max-w-5xl mx-auto text-center">
          <div className="mb-6">
            <span className="font-heading text-2xl font-bold text-gold-gradient bg-gradient-to-r from-[#C9A84C] via-[#FCD34D] to-[#C9A84C] bg-clip-text text-transparent">
              NEUROVITALE™
            </span>
            <p className="text-sm text-[#64748B] mt-1">by Visita Médica Online</p>
          </div>
          
          <p className="text-xs text-[#94A3B8] max-w-3xl mx-auto mb-6 leading-relaxed">
            <strong className="text-[#E2E8F0]">Medical Context:</strong> The NeuroVitale™ Five Elements Reading is an initial elemental medical assessment 
            created by Dr. Camila Trabucco, MD. It maps your constitutional pattern, stress axes and lifestyle priorities as a structured 
            starting point before laboratory work or full physical examination. In private coaching, this blueprint is combined with labs, 
            anamnesis and in-person assessment for comprehensive care. It does not replace emergency care or your local physician — 
            it is the first step of Elemental Medicine, the framework designed to understand how intrinsic energy drives biology, 
            longevity and performance.
          </p>
          
          <p className="text-xs text-[#64748B]">
            © {new Date().getFullYear()} NeuroVitale™ — All Rights Reserved
          </p>
        </div>
      </footer>

      {/* Feedback Modal */}
      {showFeedback && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" data-testid="feedback-modal">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass rounded-2xl p-6 md:p-8 max-w-lg w-full relative"
          >
            <button 
              onClick={() => {
                setShowFeedback(false);
                window.history.replaceState({}, '', window.location.pathname);
              }}
              className="absolute top-4 right-4 text-[#94A3B8] hover:text-white transition-colors"
              data-testid="feedback-close-btn"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="text-center mb-6">
              <h2 className="font-heading text-2xl font-semibold text-[#E2E8F0] mb-2">
                Share Your Experience
              </h2>
              <p className="text-[#94A3B8] text-sm">
                Your feedback helps us improve NeuroVitale™
              </p>
            </div>
            
            <form onSubmit={handleFeedbackSubmit} className="space-y-6">
              {/* Process Rating */}
              <div>
                <Label className="form-label mb-3 block">
                  How did you feel about the overall process of getting your blueprint?
                </Label>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFeedbackData(prev => ({ ...prev, process_rating: star }))}
                      className={`text-3xl transition-all ${
                        star <= feedbackData.process_rating ? 'text-[#C9A84C] scale-110' : 'text-[#64748B] hover:text-[#94A3B8]'
                      }`}
                      data-testid={`feedback-star-${star}`}
                    >
                      ⭐
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Blueprint Helpful */}
              <div>
                <Label className="form-label mb-3 block">
                  Did the blueprint feel deep, specific and helpful for your life and decisions?
                </Label>
                <div className="flex justify-center gap-4">
                  <Button
                    type="button"
                    onClick={() => setFeedbackData(prev => ({ ...prev, blueprint_helpful: true }))}
                    className={`px-6 py-2 rounded-lg transition-all ${
                      feedbackData.blueprint_helpful 
                        ? 'bg-[#C9A84C] text-[#0A0A0F]' 
                        : 'bg-white/10 text-[#94A3B8] hover:bg-white/20'
                    }`}
                    data-testid="feedback-helpful-yes"
                  >
                    ✅ Yes
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setFeedbackData(prev => ({ ...prev, blueprint_helpful: false }))}
                    className={`px-6 py-2 rounded-lg transition-all ${
                      !feedbackData.blueprint_helpful 
                        ? 'bg-[#C9A84C] text-[#0A0A0F]' 
                        : 'bg-white/10 text-[#94A3B8] hover:bg-white/20'
                    }`}
                    data-testid="feedback-helpful-no"
                  >
                    ❌ No
                  </Button>
                </div>
              </div>
              
              {/* Most Impactful Part */}
              <div>
                <Label className="form-label">
                  Which part impacted you the most? (Optional)
                </Label>
                <Textarea
                  value={feedbackData.most_impactful_part}
                  onChange={(e) => setFeedbackData(prev => ({ ...prev, most_impactful_part: e.target.value }))}
                  placeholder="e.g., The Five Elements diagnosis, the ancestral analysis, the nutrition protocol..."
                  className="form-input min-h-[80px]"
                  data-testid="feedback-impactful"
                />
              </div>
              
              {/* Additional Comments */}
              <div>
                <Label className="form-label">
                  Any other comments? (Optional)
                </Label>
                <Textarea
                  value={feedbackData.additional_comments}
                  onChange={(e) => setFeedbackData(prev => ({ ...prev, additional_comments: e.target.value }))}
                  placeholder="Share any thoughts, suggestions, or experiences..."
                  className="form-input min-h-[80px]"
                  data-testid="feedback-comments"
                />
              </div>
              
              <Button
                type="submit"
                disabled={feedbackLoading}
                className="btn-primary w-full py-3"
                data-testid="feedback-submit-btn"
              >
                {feedbackLoading ? "Submitting..." : "Submit Feedback"}
              </Button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
